import type { Driver } from 'driver.js'
import type { GlobalTutorialDataKeys } from '../model'
import type { OrderDetails } from '@/entities/order/model'
import type { StateConfig } from '@/entities/state/model'
import {
  mockTutorialSafeArrivedOrders,
  mockTutorialSafeDeliveringOrders,
} from '@/shared/lib/test/mocks/api/dashboardOrders.mocks'
import { stateDefinitionConfigMock } from '@/shared/lib/test/mocks/api/definitions.mocks'
import { mockGroupPartnerOnTheWayOrders, mockOnTheWayOrder } from '@/shared/lib/test/mocks/api/orders.mocks'
import { storeMock } from '@/shared/lib/test/mocks/api/store.mocks'

/**
 * Tutorial-specific state config — mirrors the pattern from `tutorialHandlers.ts`.
 * Uses a minimal three-column layout (On The Way / Arrived / Delivering) that
 * matches the tour's `section-*` anchors without a bottom carousel region.
 * Avoids the `dashboardLayoutThreeColumnWithBottom` shape from
 * `stateDefinitionConfigMock` which can interfere with `waitForOrderCards`.
 */
const tutorialStateConfig: StateConfig = {
  ...stateDefinitionConfigMock,
  layout: {
    middle: {
      columnOrder: 'row',
      columns: [
        {
          id: 'on-the-way',
          name: 'On The Way',
          states: ['OnTheWay', 'TempoOnTheWay', 'PartnerOnTheWay'],
          mobileViewComponent: 'default',
        },
        {
          id: 'arrived',
          name: 'Arrived',
          states: ['Arrived'],
          mobileViewComponent: 'default',
        },
        {
          id: 'delivering',
          name: 'Delivering',
          states: ['DeliveringToCustomer'],
          mobileViewComponent: 'default',
        },
      ],
    },
  },
  orderPanels: [
    { id: 'ready-to-pick', label: 'Ready to pick', states: ['ReadyToPick'] },
  ],
}

// Get the correct ON_THE_WAY status for consistency
const stateNames = stateDefinitionConfigMock.states.map((s: { name: string }) => s.name)
const ON_THE_WAY = stateNames.find((s: string) => s === 'OnTheWay') || stateNames[2]

// Constants for tutorial data
const TUTORIAL_GROUP_ID = 'GRP-20250128-001'
const TUTORIAL_STEP_8_ORDER_ID = '100001'

// Tutorial group order data
const TUTORIAL_GROUP_ORDERS = [
  {
    customerName: 'Alex Martinez',
    orderId: '100501',
    driverName: 'Alex Martinez',
    carPlate: 'DLV888',
  },
] as const

/**
 * Creates a tutorial-safe group order with consistent structure
 */
function createTutorialGroupOrder(orderData: typeof TUTORIAL_GROUP_ORDERS[number]): OrderDetails {
  return {
    ...mockGroupPartnerOnTheWayOrders[0],
    status: ON_THE_WAY,
    isAcknowledged: true,
    customerName: orderData.customerName,
    orderId: orderData.orderId,
    hsFields: {
      ...mockGroupPartnerOnTheWayOrders[0].hsFields,
      'orderGroupId': TUTORIAL_GROUP_ID,
      'Customer Name': orderData.customerName,
      'Order Number': orderData.orderId,
      'Driver Name': orderData.driverName,
      ...(orderData.carPlate && { 'Car Plate': orderData.carPlate }),
    },
  }
}

/**
 * Creates the step 8 tutorial order
 */
function createStep8TutorialOrder(): OrderDetails {
  return {
    ...mockOnTheWayOrder,
    customerName: 'Sarah Johnson',
    isAcknowledged: true, // Prevents pulsing animation during tutorial
    hsFields: {
      ...mockOnTheWayOrder.hsFields,
      'Customer Name': 'Sarah Johnson',
      'Order Number': mockOnTheWayOrder.orderId, // Keep the original order ID (100001)
    },
  }
}

/**
 * Gets all tutorial orders (used by multiple handlers)
 */
export function getAllTutorialOrders(): OrderDetails[] {
  const tutorialGroupOrders = TUTORIAL_GROUP_ORDERS.map(createTutorialGroupOrder)

  return [
    // Only show 1 on the way order (the step 8 tutorial order)
    ...tutorialGroupOrders,
    mockTutorialSafeArrivedOrders[0], // Only show 1 arrived order
    mockTutorialSafeDeliveringOrders[0], // Only show 1 delivering order
    createStep8TutorialOrder(),
  ]
}

/**
 * Finds an order by token or orderId from tutorial data
 */
export function findTutorialOrder(identifier: string): OrderDetails | undefined {
  if (identifier === TUTORIAL_STEP_8_ORDER_ID) {
    return createStep8TutorialOrder()
  }

  const allOrders = getAllTutorialOrders()

  return allOrders.find(order =>
    order.orderId === identifier
    || order.installRef === identifier,
  ) || createStep8TutorialOrder() // Fallback to step 8 tutorial order
}

/**
 * Enables tutorial mode by setting global window variables
 */
export async function enableTutorialMode(): Promise<boolean> {
  try {
    if (typeof window !== 'undefined') {
      window.__tutorialMode = true
      window.__tutorialData = {
        orders: getAllTutorialOrders(),
        stateConfig: tutorialStateConfig,
        storeDetails: storeMock,
        waitTime: {
          waitTimeAverage: 180, // 3 minutes in seconds
          numberOfOrders: 3,
        },
      }
    }
    return true
  } catch (error) {
    console.error('Failed to enable tutorial mode:', error)
    return false
  }
}

/**
 * Disables tutorial mode by clearing global window variables
 */
export async function disableTutorialMode(): Promise<boolean> {
  try {
    if (typeof window !== 'undefined') {
      delete window.__tutorialMode
      delete window.__tutorialData
    }
    return true
  } catch (error) {
    console.error('Error disabling tutorial mode:', error)
    return false
  }
}

/**
 * Injects the tutorial header style into the document
 */
export function injectTutorialHeaderStyle(headerHeight: number) {
  if (typeof document !== 'undefined' && !document.getElementById('tutorial-header-style')) {
    const tutorialStyles = document.createElement('style')
    tutorialStyles.id = 'tutorial-header-style'
    tutorialStyles.textContent = `body.tutorial-header-active { padding-top: ${headerHeight}px; }`
    document.head.appendChild(tutorialStyles)
  }
}

/**
 * Destroys the driver instances safely
 */
export function destroyDriverInstances(driverRef: React.RefObject<Driver | null>, activeDriverRef: React.RefObject<Driver | null>) {
  if (driverRef.current) {
    driverRef.current.destroy()
    driverRef.current = null
  }
  if (activeDriverRef.current && activeDriverRef.current !== driverRef.current) {
    activeDriverRef.current.destroy()
    activeDriverRef.current = null
  }
}

/**
 * Handles tutorial mode logic - returns tutorial data if available, otherwise executes fallback function
 * @param tutorialDataKey - The key of the tutorial data to retrieve
 * @param fallbackFn - The function to execute if not in tutorial mode or data is not available
 * @returns The result of either the tutorial data or the fallback function
 */
export async function withTutorialFallback<T>(
  tutorialDataKey: GlobalTutorialDataKeys,
  fallbackFn: () => Promise<T>,
): Promise<T> {
  // Check if tutorial mode is active
  if (typeof globalThis.window !== 'undefined' && globalThis.window.__tutorialMode) {
    const tutorialData = window.__tutorialData?.[tutorialDataKey] as T | undefined
    if (tutorialData) {
      return tutorialData
    }
  }

  return await fallbackFn()
}
